import React, { useEffect, useState, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, Clock, AlertTriangle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AnalyticsOfferSuccessPage = () => {
  const { token } = useParams();
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const [status, setStatus] = useState('checking'); // checking | paid | failed | timeout
  const [attempts, setAttempts] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!sessionId) {
      setStatus('failed');
      return;
    }

    const poll = async () => {
      try {
        const { data } = await axios.get(
          `${API}/analytics-offer/${token}/payment-status/${sessionId}`
        );
        if (data.payment_status === 'paid') {
          setStatus('paid');
          return;
        }
        if (data.payment_status === 'expired' || data.payment_status === 'canceled') {
          setStatus('failed');
          return;
        }
      } catch {
        // keep retrying
      }
      setAttempts((a) => {
        const next = a + 1;
        if (next >= 8) {
          setStatus('timeout');
          return next;
        }
        timerRef.current = setTimeout(poll, 2500);
        return next;
      });
    };

    poll();
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [token, sessionId]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-violet-50 to-fuchsia-50 px-4">
      <div className="max-w-md w-full bg-white rounded-3xl shadow-xl border border-slate-200 p-10 text-center" data-testid="offer-success-page">
        {status === 'checking' && (
          <>
            <div className="w-16 h-16 bg-violet-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Clock className="w-8 h-8 text-violet-600 animate-pulse" />
            </div>
            <h1 className="text-2xl font-black text-slate-900 mb-2">Confirming your payment…</h1>
            <p className="text-slate-600">Stripe is processing your subscription. This usually takes a few seconds.</p>
          </>
        )}
        {status === 'paid' && (
          <>
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-9 h-9 text-emerald-600" />
            </div>
            <h1 className="text-2xl font-black text-slate-900 mb-2">Subscription active!</h1>
            <p className="text-slate-600 mb-6">
              Welcome aboard. Your first Visitor Intelligence report will arrive at the cadence in your agreement. We'll send a confirmation email shortly.
            </p>
            <p className="text-xs text-slate-500">You can close this window — billing continues automatically until you cancel.</p>
          </>
        )}
        {status === 'failed' && (
          <>
            <div className="w-16 h-16 bg-rose-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="w-8 h-8 text-rose-500" />
            </div>
            <h1 className="text-2xl font-black text-slate-900 mb-2">Payment didn't complete</h1>
            <p className="text-slate-600 mb-6">
              No charge was made. Please return to the offer page and try again, or contact the Audiostride team.
            </p>
            <a
              href={`/analytics-offer/${token}`}
              className="inline-block px-6 py-3 rounded-full bg-violet-600 hover:bg-violet-700 text-white font-bold"
              data-testid="retry-payment-link"
            >
              Try again
            </a>
          </>
        )}
        {status === 'timeout' && (
          <>
            <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Clock className="w-8 h-8 text-amber-600" />
            </div>
            <h1 className="text-2xl font-black text-slate-900 mb-2">Still confirming…</h1>
            <p className="text-slate-600 mb-6">
              This is taking longer than expected. You'll receive a confirmation email once the payment settles. You can safely close this window.
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default AnalyticsOfferSuccessPage;
